import { ApolloError, gql, useMutation } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import IconInfo from '@apollo/icons/small/IconInfo.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
} from '@apollo/orbit';
import { Switch } from '@apollo/space-kit/Switch';
import * as Sentry from '@sentry/react';
import { useFormik } from 'formik';
import { capitalize } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import coerceToSemver from 'semver/functions/coerce';
import semverGte from 'semver/functions/gte';
import * as Yup from 'yup';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { UseNewFedNextVersionAvailableFragment } from 'src/hooks/useNewFedNextVersionAvailable/useNewFedNextVersionAvailable';
import { useIsFedNextEnabled } from 'src/lib/featureFlags';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  BuildPipelineTrack,
  BuildPipelineTrackDetails,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import { getMatchingAllowedTrack } from '../FederationVersionInfo';

import { ProposedChangesSection } from './ProposedChangesSection';
import { RouterRequirements } from './RouterRequirements';

export const configureBuildPipelineModalFragment = gql`
  fragment ConfigureBuildPipelineModalFragment on GraphVariant {
    id
    name
    compositionVersion
    buildConfig {
      buildPipelineTrack
      tagInApiSchema
    }
    ...UseNewFedNextVersionAvailableFragment
  }
  ${UseNewFedNextVersionAvailableFragment}
`;

export const defaultBuildPipelineTrackMutation = gql<
  GraphQLTypes.DefaultBuildPipelineTrackMutation,
  GraphQLTypes.DefaultBuildPipelineTrackMutationVariables
>`
  mutation DefaultBuildPipelineTrackMutation(
    $graphId: ID!
    $buildPipelineTrack: BuildPipelineTrack!
  ) {
    graph(id: $graphId) {
      defaultBuildPipelineTrack(buildPipelineTrack: $buildPipelineTrack)
    }
  }
`;

export const updateBuildPipelineConfigMutation = gql<
  GraphQLTypes.UpdateBuildPipelineConfigMutation,
  GraphQLTypes.UpdateBuildPipelineConfigMutationVariables
>`
  mutation UpdateBuildPipelineConfigMutation(
    $serviceId: ID!
    $variant: String!
    $buildPipelineTrack: BuildPipelineTrack!
    $tagInApiSchema: Boolean!
    $skipLaunch: Boolean
  ) {
    service(id: $serviceId) {
      variant(name: $variant) {
        buildConfig(
          version: $buildPipelineTrack
          tagInApiSchema: $tagInApiSchema
          skipLaunch: $skipLaunch
        ) {
          id
          ...ConfigureBuildPipelineModalFragment
        }
      }
    }
  }
  ${configureBuildPipelineModalFragment}
`;

export type ProposalSourceVariantInfo = {
  name?: string;
  pipelineTrack?: GraphQLTypes.BuildPipelineTrack;
};

/**
 * Schema for the form
 */
const validationSchema = () =>
  Yup.object().shape({
    buildPipelineTrack: Yup.mixed<GraphQLTypes.BuildPipelineTrack>()
      .oneOf(Object.values(BuildPipelineTrack))
      .required(),
    tagInApiSchema: Yup.boolean().required(),
  });

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<ReturnType<typeof validationSchema>>;

export const ConfigureBuildPipelineModal = ({
  serviceId,
  variant,
  onClose,
  isOpen,
  allowedTracks: allowedTracksProp,
  isGraph,
  graphType,
  refetchGraphData,
  defaultBuildPipelineTrack,
  routerVersion,
  canUpdateFedNextVersion,
  // this used by proposals to display the current source variant pipeline track
  // otherwise it can be ignored
  sourceVariantInfo,
  skipLaunch,
}: {
  serviceId: string;
  variant: GraphQLTypes.ConfigureBuildPipelineModalFragment;
  onClose: () => void;
  isOpen: boolean;
  allowedTracks: GraphQLTypes.BuildPipelineTrackDetails[];
  isGraph?: boolean;
  graphType: GraphQLTypes.GraphType;
  refetchGraphData: () => void;
  defaultBuildPipelineTrack?: GraphQLTypes.BuildPipelineTrack;
  routerVersion?: string;
  canUpdateFedNextVersion?: boolean;
  // this used by proposals to display the current source variant pipeline track
  // otherwise it can be ignored
  sourceVariantInfo?: ProposalSourceVariantInfo;
  skipLaunch?: boolean;
}) => {
  const [error, setError] = useState<ApolloError | undefined>(undefined);
  const [advancedOptionsDrawerOpen, setAdvancedOptionsDrawerOpen] = useState(
    !variant.buildConfig?.tagInApiSchema,
  );
  const [showProposedChanges, setShowProposedChanges] = useState(false);
  const allowFedNextAsGraphDefault = useIsFedNextEnabled();

  const isFedNext = variant?.compositionVersion === BuildPipelineTrack.FED_NEXT;

  const [updateBuildPipelineConfig] = useMutation(
    updateBuildPipelineConfigMutation,
    {
      onCompleted: () => {
        refetchGraphData();
        resetForm({});
        setShowProposedChanges(false);
        onClose();
      },
      onError: (e) => {
        setError(e);
      },
    },
  );

  const [updateDefaultPipelineTrack] = useMutation(
    defaultBuildPipelineTrackMutation,
    {
      onCompleted: () => {
        refetchGraphData();
        resetForm({});
        setShowProposedChanges(false);
        onClose();
      },
      onError: (e) => {
        setError(e);
      },
    },
  );

  // Hide tracks which are unsupported by cloudy graph routers
  // Hide `FED_NEXT` track from the graph's default build pipeline
  const allowedTracks = useMemo(() => {
    const allowedTracksForGraphType =
      routerVersion === undefined ||
      graphType !== GraphQLTypes.GraphType.CLOUD_SUPERGRAPH
        ? allowedTracksProp
        : allowedTracksProp.filter((track) => {
            const trackMinRouterVersion = coerceToSemver(
              track.minimumRouterVersion,
            );
            return (
              trackMinRouterVersion !== null &&
              semverGte(routerVersion, trackMinRouterVersion)
            );
          });
    return isGraph
      ? allowedTracksForGraphType.filter(
          (track) =>
            track.buildPipelineTrack !== BuildPipelineTrack.FED_NEXT ||
            (track.buildPipelineTrack === BuildPipelineTrack.FED_NEXT &&
              allowFedNextAsGraphDefault),
        )
      : allowedTracksForGraphType;
  }, [
    allowFedNextAsGraphDefault,
    allowedTracksProp,
    graphType,
    isGraph,
    routerVersion,
  ]);

  const matchingTrack = getMatchingAllowedTrack(
    allowedTracks,
    isGraph
      ? defaultBuildPipelineTrack
      : variant.buildConfig?.buildPipelineTrack,
  );

  const [minimumRouterVersion, setMinimumRouterVersion] = useState(
    matchingTrack?.minimumRouterVersion || '1.0',
  );
  const isCloudGraph = graphType === GraphQLTypes.GraphType.CLOUD_SUPERGRAPH;
  const sortedAllowedTracks = Array.from(allowedTracks).reverse();

  const defaultFormBuildPipelineTrack = (allowedTracks
    .filter(
      (track) =>
        track?.buildPipelineTrack &&
        track?.buildPipelineTrack !== GraphQLTypes.BuildPipelineTrack.FED_NEXT,
    )
    .pop()?.buildPipelineTrack ??
    GraphQLTypes.BuildPipelineTrack
      .FED_NEXT) as GraphQLTypes.BuildPipelineTrack;

  const {
    values,
    setFieldValue,
    handleChange,
    handleSubmit,
    isSubmitting,
    isValid,
    resetForm,
  } = useFormik<FormValues>({
    initialValues: {
      buildPipelineTrack:
        matchingTrack?.buildPipelineTrack ?? defaultFormBuildPipelineTrack,
      tagInApiSchema: variant.buildConfig?.tagInApiSchema ?? false,
    },
    async onSubmit(formValues) {
      setError(undefined);
      try {
        if (isGraph) {
          await updateDefaultPipelineTrack({
            variables: {
              graphId: serviceId,
              buildPipelineTrack: formValues.buildPipelineTrack,
            },
          });
        } else {
          await updateBuildPipelineConfig({
            variables: {
              serviceId,
              variant: variant.name,
              buildPipelineTrack: formValues.buildPipelineTrack,
              tagInApiSchema: formValues.tagInApiSchema,
              skipLaunch: skipLaunch ?? false,
            },
          });
        }
      } catch (e) {
        // We don't need to do anything to handle this here since the error will
        // come back in the `error` from our useMutation hook
        Sentry.captureException(e);
      }
    },
    validationSchema,
  });

  const onCloseModal = useCallback(() => {
    onClose();
    resetForm({});
    setShowProposedChanges(false);
  }, [onClose, resetForm]);

  return (
    <Modal size="3xl" onClose={onCloseModal} isOpen={isOpen}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Configure build pipeline</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <FormControl isRequired id="buildPipelineTrack">
            <div className="flex flex-row justify-between">
              <FormLabel>
                Federation version
                <Tooltip
                  placement="right-start"
                  label={
                    <>
                      <p>
                        The version of Apollo Federation that GraphOS uses to
                        compose your supergraph schema.
                      </p>
                      <br />
                      <p>
                        Later versions support additional directives and other
                        features.
                      </p>
                    </>
                  }
                >
                  <IconInfo className="ml-2" />
                </Tooltip>
              </FormLabel>
              {!isCloudGraph && (
                <Link isExternal to={DocsRouter.path('FederationChangelog')}>
                  View changelog
                </Link>
              )}
            </div>
            <Select
              data-testid="buildPipelineTrack"
              value={values.buildPipelineTrack}
              onChange={(event) => {
                setError(undefined);
                handleChange(event);
                setShowProposedChanges(true);

                const matchingSelectedTrack = getMatchingAllowedTrack(
                  allowedTracks,
                  event.currentTarget.value,
                ) as BuildPipelineTrackDetails;
                setMinimumRouterVersion(
                  matchingSelectedTrack.minimumRouterVersion || '1.0',
                );
              }}
            >
              {sortedAllowedTracks.map(
                (track: GraphQLTypes.BuildPipelineTrackDetails) => {
                  let badge = track.badge ? capitalize(track.badge) : null;
                  if (
                    sourceVariantInfo?.name &&
                    sourceVariantInfo?.pipelineTrack &&
                    track.buildPipelineTrack === sourceVariantInfo.pipelineTrack
                  ) {
                    badge = badge
                      ? `${badge} - ${sourceVariantInfo.name}`
                      : sourceVariantInfo.name;
                  }
                  return (
                    <option
                      key={track.buildPipelineTrack}
                      value={track.buildPipelineTrack}
                    >
                      {track.displayName}
                      {badge && ` (${badge})`}
                    </option>
                  );
                },
              )}
            </Select>
          </FormControl>
          {showProposedChanges && matchingTrack && (
            <ProposedChangesSection
              currentVersionTrack={matchingTrack}
              proposedVersionTrack={
                getMatchingAllowedTrack(
                  allowedTracks,
                  values.buildPipelineTrack,
                ) as BuildPipelineTrackDetails
              }
              error={error}
              isGraph={isGraph}
            />
          )}
          {!isCloudGraph && minimumRouterVersion && (
            <RouterRequirements minimumRouterVersion={minimumRouterVersion} />
          )}
          {!isGraph && (
            <div>
              <Button
                variant="hidden"
                className="-ml-3 text-sm font-normal hover:bg-white"
                onClick={() => setAdvancedOptionsDrawerOpen((prev) => !prev)}
                rightIcon={
                  advancedOptionsDrawerOpen ? (
                    <IconChevronUp />
                  ) : (
                    <IconChevronDown />
                  )
                }
              >
                Advanced options
              </Button>
              {advancedOptionsDrawerOpen && (
                <div className="flex">
                  <div className="flex items-center">
                    <span className="h-fit rounded-md border border-primary px-2 py-1 text-sm font-semibold">
                      @tag
                    </span>
                    <p className="ml-2 text-neutral">
                      Show all uses of @tag directives to consumers in Schema
                      Reference and Explorer
                    </p>
                  </div>
                  <Switch
                    aria-label="toggle tag directive support"
                    className="ml-auto"
                    size="normal"
                    showTextualState={false}
                    isSelected={values.tagInApiSchema}
                    onChange={(value: boolean) => {
                      setFieldValue('tagInApiSchema', value);
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <Alert status="info">
            Changing your Federation version automatically initiates a build
            using the new version.
          </Alert>
        </ModalBody>
        <ModalFooter>
          {isCloudGraph ? (
            <Link
              isExternal
              to={DocsRouter.path('FederationChangelog')}
              className="mr-auto"
            >
              View federation changelog
            </Link>
          ) : (
            <Link
              isExternal
              to="https://www.apollographql.com/docs/graphos/graphs/updating/"
              className="mr-auto"
            >
              Learn how to upgrade safely
            </Link>
          )}
          <ButtonGroup>
            <Button variant="secondary" type="button" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              isDisabled={
                // form values are invalid
                !isValid ||
                (isFedNext
                  ? // if BPT is currently fed next but there is no new version available and fed next is currently selected
                    !canUpdateFedNextVersion &&
                    values.buildPipelineTrack === BuildPipelineTrack.FED_NEXT
                  : matchingTrack?.buildPipelineTrack ===
                      values.buildPipelineTrack &&
                    variant.buildConfig?.tagInApiSchema ===
                      values.tagInApiSchema)
              }
              isLoading={isSubmitting}
              loadingText={error ? 'Retrying' : 'Saving'}
              onClick={() => handleSubmit()}
            >
              {error ? 'Retry' : 'Save'}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
